/**
 * KB-3306 — the per-field ownership badge, rendered right of a governed field's label.
 *
 * Reads the field's record from the nearest `FieldStateProvider` (via `useFieldState`), asks the pure
 * `describeBadge` for the copy, resolves the winner's first name from `users/<uid>` (cached, denied
 * lookups never print the uid), and renders. Staff audience also gets a lock / unlock control; the
 * director audience does not (AC2). Renders nothing when there is no record, a legacy winner, or no
 * provider in the tree — so it is safe to wire beside any label.
 *
 * ── Why the split into BadgeView ──────────────────────────────────────────────────────────────────
 * The outer component uses only `useFieldState` (whose context defaults safely outside a provider). It
 * returns null the moment there is no badge to show, so `useUserCache` — which THROWS outside a
 * `UserCacheProvider` — is called only from `BadgeView`, i.e. only when a badge actually renders. A
 * governed field label can therefore be wired into a form section whose isolated tests mount no
 * UserCacheProvider (the whole app is wrapped in one at runtime) without crashing them.
 */
import React, { useEffect, useState } from "react";
import { Lock, Unlock } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

import { toast } from "@/components/ui/sonner";
import { useUserCache } from "@/context/UserCacheContext";
import {
  actorUidOrNull,
  describeBadge,
  fetchFieldContribution,
  setCampDataFieldPolicy,
  shouldShowVendorHint,
  useFieldState,
  type BadgeAudience,
  type BadgeDescriptor,
  type FieldEntityKind,
  type FieldRecord,
} from "@/services/campDataFieldState";

interface FieldOwnershipBadgeProps {
  fieldPath: string;
  /** Overrides the provider's audience; falls back to the FieldStateProvider context, then "staff". */
  audience?: BadgeAudience | null;
  /** Overrides the provider's viewer uid (holder match); falls back to the context. */
  currentUid?: string | null;
}

/** Format a vendor value for the "Locked by the camp" line (truncated; never a raw uid or object dump). */
function fmtVendorValue(v: unknown): string {
  if (v == null) return "(empty)";
  if (typeof v === "string") return v.length > 140 ? `${v.slice(0, 140)}…` : v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 140 ? `${s.slice(0, 140)}…` : s;
  } catch {
    return "(complex value)";
  }
}

// Shown in place of a name we cannot resolve (a denied lookup, an absent user, or a non-uid actor such
// as VERDICT_BY "nicholas"). Never the uid (brief: "A denied lookup must not print the uid").
const NAME_FALLBACK = "a teammate";

function fmtDate(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : format(d, "MMM d, yyyy");
}
function fmtRelative(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : formatDistanceToNow(d, { addSuffix: true });
}

function renderBadgeText(desc: BadgeDescriptor, name: string): string {
  return desc.text
    .replace("{name}", name)
    .replace("{date}", fmtDate(desc.atIso))
    .replace("{relative}", fmtRelative(desc.atIso))
    .replace(/\s+/g, " ")
    .trim();
}

interface BadgeViewProps {
  desc: BadgeDescriptor;
  audience: BadgeAudience;
  fieldPath: string;
  isLocked: boolean;
  entity: { kind: FieldEntityKind; id: string } | null;
  reload: () => void;
}

const BadgeView: React.FC<BadgeViewProps> = ({ desc, audience, fieldPath, isLocked, entity, reload }) => {
  const { getUserData } = useUserCache();
  const [name, setName] = useState<string>(NAME_FALLBACK);
  const [busy, setBusy] = useState<boolean>(false);

  const uid = actorUidOrNull(desc.actorId);
  useEffect(() => {
    if (!uid) {
      setName(NAME_FALLBACK);
      return;
    }
    let mounted = true;
    getUserData(uid)
      .then((u) => {
        if (!mounted) return;
        if (u && !u.unavailable && (u.firstName || u.lastName)) {
          setName(u.firstName || u.lastName || NAME_FALLBACK);
        } else {
          setName(NAME_FALLBACK);
        }
      })
      .catch(() => {
        if (mounted) setName(NAME_FALLBACK);
      });
    return () => {
      mounted = false;
    };
  }, [uid, getUserData]);

  const showLock = audience === "staff" && !!entity;

  async function toggleLock() {
    if (!entity) return;
    setBusy(true);
    try {
      await setCampDataFieldPolicy({ entity, fieldPath, action: isLocked ? "unlock" : "lock" });
      toast.success(isLocked ? "Field unlocked — the pipeline can propose again" : "Field locked — vendor updates ignored");
      reload();
    } catch {
      toast.error(`Could not ${isLocked ? "unlock" : "lock"} this field`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="field-ownership-badge" data-field-path={fieldPath}>
      <span title={desc.hover}>{renderBadgeText(desc, name)}</span>
      {showLock && (
        <button
          type="button"
          onClick={toggleLock}
          disabled={busy}
          className="inline-flex items-center text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label={isLocked ? "Unlock field" : "Lock field"}
          title={isLocked ? "Unlock — vendor updates can propose again" : "Lock — ignore vendor updates for this field"}
        >
          {isLocked ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
        </button>
      )}
    </span>
  );
};

// The staff view of a director-locked field the vendor now disagrees with (KB-3318): an actionable
// "Locked by the camp · vendor first saw <value> <date> · [Unlock and propose]". The state doc holds
// only the pipeline value's hash, so the value is read lazily from `fieldContributions/<cid>` (same as
// FieldVendorHint). "first saw", never "now" — `latestPipeline.observedAt` is FIRST-seen (ruling 2).
// Until that value is VERIFIED (loading, a missing contribution doc, or a failed read) it falls back to
// the standard director-lock badge rather than rendering "vendor first saw (empty)" — never render an
// absence we did not verify.
interface LockedByCampViewProps {
  record: FieldRecord;
  fieldPath: string;
  entity: { kind: FieldEntityKind; id: string };
  reload: () => void;
  fallback: React.ReactElement | null;
}

const LockedByCampView: React.FC<LockedByCampViewProps> = ({ record, fieldPath, entity, reload, fallback }) => {
  const [value, setValue] = useState<unknown>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const cid = record.latestPipeline?.cid;

  useEffect(() => {
    if (!cid) {
      setLoaded(false);
      setValue(undefined);
      return;
    }
    let mounted = true;
    fetchFieldContribution(cid)
      .then((row) => {
        if (!mounted) return;
        if (!row) {
          setLoaded(false);
          setValue(undefined);
          return;
        }
        setValue(row.value);
        setLoaded(true);
      })
      .catch(() => {
        if (mounted) setLoaded(false);
      });
    return () => {
      mounted = false;
    };
  }, [cid]);

  if (!loaded) return fallback;

  const firstSeen = fmtDate(record.latestPipeline?.observedAt);

  async function unlockAndPropose() {
    setBusy(true);
    try {
      await setCampDataFieldPolicy({ entity, fieldPath, action: "unlock" });
      toast.success("Field unlocked — the vendor value can be proposed on the next run");
      reload();
    } catch {
      toast.error("Could not unlock this field");
    } finally {
      setBusy(false);
    }
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-muted-foreground"
      data-testid="field-locked-by-camp"
      data-field-path={fieldPath}
    >
      <span>
        Locked by the camp · vendor first saw{" "}
        <span className="font-medium text-foreground">{fmtVendorValue(value)}</span>
        {firstSeen ? ` ${firstSeen}` : ""} ·{" "}
      </span>
      <button
        type="button"
        onClick={unlockAndPropose}
        disabled={busy}
        className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
      >
        Unlock and propose
      </button>
    </span>
  );
};

export const FieldOwnershipBadge: React.FC<FieldOwnershipBadgeProps> = ({
  fieldPath,
  audience: audienceProp,
  currentUid: currentUidProp,
}) => {
  const { record, entity, reload, audience: ctxAudience, currentUid: ctxUid } = useFieldState(fieldPath);
  // Explicit prop wins; else the provider's audience (which defaults to "staff", or is null in the
  // welcome flow before the viewer's role resolves). `??` is WRONG here: a null context audience is
  // MEANINGFUL ("render nothing"), and `undefined ?? null ?? "staff"` would coalesce it to "staff" and
  // flash a staff badge + lock control at a director. Distinguish an absent prop (undefined) from an
  // explicit null; ctxAudience is never undefined (EMPTY_CONTEXT defaults it).
  const audience: BadgeAudience | null = audienceProp !== undefined ? audienceProp : ctxAudience;
  const currentUid = currentUidProp ?? ctxUid ?? null;

  if (!audience || !record) return null;

  const desc = describeBadge(record, audience, currentUid);
  if (!desc) return null;

  const standardBadge = (
    <BadgeView
      desc={desc}
      audience={audience}
      fieldPath={fieldPath}
      isLocked={record.policy === "locked"}
      entity={entity}
      reload={reload}
    />
  );

  // Staff view of a director-locked field the vendor now disagrees with → the actionable state.
  if (
    audience === "staff" &&
    record.policy === "locked" &&
    record.lock?.class === "director" &&
    shouldShowVendorHint(record) &&
    entity
  ) {
    return (
      <LockedByCampView record={record} fieldPath={fieldPath} entity={entity} reload={reload} fallback={standardBadge} />
    );
  }

  return standardBadge;
};

export default FieldOwnershipBadge;
